/**
 * Component: Info Tooltip
 * -----------------------------------------------------------------------------
 * - A lightweight tooltip component that displays on click (default) or hover.
 * - Supports keyboard accessibility (Escape to close) and ensures only one
 *   tooltip is visible at a time.
 * - Auto-positions based on viewport bounds.
 *
 * ## Usage Example:
 * <info-tooltip
 *   class="info-tooltip"
 *   data-hover
 * >
 *   <button
 *     type="button"
 *     class="info-tooltip__button"
 *     data-js-tooltip-button
 *     aria-describedby="tooltip-sample"
 *   >
 *     {% render 'icon-info' %}
 *   </button>
 *   <span
 *     id="tooltip-sample"
 *     role="tooltip"
 *     class="info-tooltip__content"
 *     data-js-tooltip-content
 *   >
 *     Tooltip content goes here.
 *   </span>
 * </info-tooltip>
 */

;(() => {
  if (customElements.get('info-tooltip')) {
    return
  }

  class InfoTooltip extends HTMLElement {
    /**
     * STATIC PROPERTIES & CONFIGURATION
     * -------------------------------------------------------------------------
     */
    static _attrs = {
      hover: 'data-hover',
    }

    static _classes = {
      visible: 'is-visible',
      flipLeft: 'is-flipped-left',
      flipTop: 'is-flipped-top',
      flipBottom: 'is-flipped-bottom',
    }

    static _selectors = {
      button: '[data-js-tooltip-button]',
      content: '[data-js-tooltip-content]',
    }

    /**
     * CONSTRUCTOR & LIFECYCLE
     * -------------------------------------------------------------------------
     */
    constructor() {
      super()

      /** @type {HTMLElement|null} */
      this.button = null

      /** @type {HTMLElement|null} */
      this.tooltip = null

      // Bind event handlers to preserve context
      this.onPointerDown = this.pointerDownHandler.bind(this)
      this.onOutsideClick = this.outsideClickHandler.bind(this)
      this.onEscape = this.escapeHandler.bind(this)
      this.onMouseEnter = this.mouseEnterHandler.bind(this)
      this.onMouseLeave = this.mouseLeaveHandler.bind(this)
    }

    /**
     * Lifecycle: Called when element is connected to DOM
     */
    connectedCallback() {
      this.initialize()
    }

    /**
     * Lifecycle: Called when element is removed from DOM
     */
    disconnectedCallback() {
      this.cleanup()
    }

    /**
     * INITIALIZATION & CLEANUP
     * -------------------------------------------------------------------------
     */

    /**
     * Initialize the tooltip component
     */
    initialize() {
      this.setElements()
      this.addEventListeners()
    }

    /**
     * Cleanup when component is removed
     */
    cleanup() {
      this.removeEventListeners()
    }

    /**
     * Query and store references to button and tooltip elements
     */
    setElements() {
      this.button = this.querySelector(InfoTooltip._selectors.button)
      this.tooltip = this.querySelector(InfoTooltip._selectors.content)
    }

    /**
     * EVENT HANDLING
     * -------------------------------------------------------------------------
     */

    /**
     * Add all necessary event listeners
     */
    addEventListeners() {
      if (!this.button || !this.tooltip) {
        return
      }

      const prefersHover = this.hasAttribute(InfoTooltip._attrs.hover)

      this.button.addEventListener('pointerdown', this.onPointerDown)
      document.addEventListener('pointerdown', this.onOutsideClick)
      document.addEventListener('keydown', this.onEscape)

      if (prefersHover) {
        this.button.addEventListener('mouseenter', this.onMouseEnter)
        this.button.addEventListener('mouseleave', this.onMouseLeave)
      }
    }

    /**
     * Remove event listeners
     */
    removeEventListeners() {
      if (!this.button) {
        return
      }

      const prefersHover = this.hasAttribute(InfoTooltip._attrs.hover)

      this.button.removeEventListener('pointerdown', this.onPointerDown)
      document.removeEventListener('pointerdown', this.onOutsideClick)
      document.removeEventListener('keydown', this.onEscape)

      if (prefersHover) {
        this.button.removeEventListener('mouseenter', this.onMouseEnter)
        this.button.removeEventListener('mouseleave', this.onMouseLeave)
      }
    }

    /**
     * Show the tooltip on mouse enter (hover mode)
     */
    mouseEnterHandler() {
      this.showTooltip()
    }

    /**
     * Hide the tooltip on mouse leave (hover mode)
     */
    mouseLeaveHandler() {
      this.hideTooltip()
    }

    /**
     * Toggle tooltip visibility on pointer down
     * @param {PointerEvent} event - Pointer event triggered by interaction
     */
    pointerDownHandler(event) {
      event.preventDefault()
      this.toggleTooltip()
    }

    /**
     * Hide tooltip when clicking outside the component
     * @param {Event} event - Click or pointer event
     */
    outsideClickHandler(event) {
      if (!this.contains(event.target)) {
        this.hideTooltip()
      }
    }

    /**
     * Hide tooltip when pressing the Escape key
     * @param {KeyboardEvent} event - Key press event
     */
    escapeHandler(event) {
      if (event.key === 'Escape') {
        this.hideTooltip()
      }
    }

    /**
     * CORE FUNCTIONALITY
     * -------------------------------------------------------------------------
     */

    /**
     * Show the tooltip and hide all other tooltips
     */
    showTooltip() {
      InfoTooltip.hideAllTooltips()

      this.setTooltipPosition()
      this.tooltip?.classList.add(InfoTooltip._classes.visible)
    }

    /**
     * Determine and apply tooltip position based on viewport bounds
     */
    setTooltipPosition() {
      if (!this.tooltip) {
        return
      }

      const margin = 8

      // Reset all flip classes
      this.tooltip.classList.remove(
        InfoTooltip._classes.flipLeft,
        InfoTooltip._classes.flipTop,
        InfoTooltip._classes.flipBottom
      )

      // Force a reflow to ensure the tooltip is rendered before measuring
      this.tooltip.offsetHeight

      // Get the tooltip's bounding rect after it's been rendered
      const rect = this.tooltip.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      // Check if tooltip overflows viewport bounds
      const shouldFlipLeft = rect.right > viewportWidth - margin
      const shouldFlipBottom = rect.top < margin
      const shouldFlipTop = rect.bottom > viewportHeight - margin

      if (shouldFlipLeft) {
        this.tooltip.classList.add(InfoTooltip._classes.flipLeft)
      }

      if (shouldFlipBottom) {
        this.tooltip.classList.add(InfoTooltip._classes.flipBottom)
      }

      if (shouldFlipTop) {
        this.tooltip.classList.add(InfoTooltip._classes.flipTop)
      }
    }

    /**
     * Hide the tooltip
     */
    hideTooltip() {
      this.tooltip?.classList.remove(InfoTooltip._classes.visible)
    }

    /**
     * Toggle the tooltip visibility
     */
    toggleTooltip() {
      if (!this.tooltip) {
        return
      }

      const isVisible = this.tooltip.classList.contains(
        InfoTooltip._classes.visible
      )

      if (isVisible) {
        this.hideTooltip()
      } else {
        this.showTooltip()
      }
    }

    /**
     * STATIC METHODS
     * -------------------------------------------------------------------------
     */

    /**
     * Hide all other tooltips in the document
     */
    static hideAllTooltips() {
      document.querySelectorAll('info-tooltip').forEach((el) => {
        if (el instanceof InfoTooltip && el.tooltip) {
          el.tooltip.classList.remove(InfoTooltip._classes.visible)
        }
      })
    }
  }

  customElements.define('info-tooltip', InfoTooltip)
})()
